import React, { type JSX } from "react";
import ChatbotLayout from "../../ChatbotLayout_Component";
import WorkspaceTabBar, { type WorkspaceTabBarProps } from "./WorkspaceTabBar";

const TOGGLE_BUTTON =
  "relative z-10 flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-full transition-colors duration-200";

/** iOS-style two-up switch between the chat and the results table. */
function ViewToggle({
  showChat,
  onSelectView,
}: {
  showChat: boolean;
  onSelectView: (chat: boolean) => void;
}): JSX.Element {
  const tone = (selected: boolean): string =>
    selected ? "text-cvc-blue" : "text-gray-600 hover:text-gray-800";
  return (
    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-4">
      <div className="flex justify-center">
        <div className="relative inline-flex items-center bg-gray-100 rounded-full p-1 w-72">
          {/* Background slider */}
          <div
            className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out ${
              showChat ? "translate-x-1" : "translate-x-[calc(100%+6px)]"
            }`}
          />

          <button onClick={() => onSelectView(true)} className={`${TOGGLE_BUTTON} ${tone(showChat)}`}>
            <div className="w-5 h-5 bg-cvc-blue rounded-full flex items-center justify-center mr-2">
              <span className="text-white text-xs font-medium">AI</span>
            </div>
            Chat
          </button>

          <button
            onClick={() => onSelectView(false)}
            className={`${TOGGLE_BUTTON} ${tone(!showChat)}`}
          >
            <svg
              className="w-4 h-4 mr-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z"
              />
            </svg>
            Table
          </button>
        </div>
      </div>
    </div>
  );
}

/** Full-screen results view: the tab strip above the active panel. */
function MobileResults({
  tabs,
  content,
}: {
  tabs: WorkspaceTabBarProps;
  content: JSX.Element | null;
}): JSX.Element {
  return (
    <div className="h-full flex flex-col">
      {/* Tab navigation — scrolls horizontally on mobile */}
      <div className="flex-shrink-0 border-b border-cvc-grey overflow-x-auto bg-white">
        <div className="flex min-w-max">
          <WorkspaceTabBar variant="mobile" tabs={tabs} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">{content}</div>
    </div>
  );
}

/** The narrow-viewport layout: one pane at a time, chosen by the toggle. */
export default function MobileDealsView({
  showChat,
  onSelectView,
  tabs,
  content,
}: {
  showChat: boolean;
  onSelectView: (chat: boolean) => void;
  tabs: WorkspaceTabBarProps;
  content: JSX.Element | null;
}): JSX.Element {
  return (
    <div className="md:hidden h-full flex flex-col">
      <ViewToggle showChat={showChat} onSelectView={onSelectView} />

      <div className="flex-1 min-h-0">
        {showChat ? (
          <ChatbotLayout chatInputAriaLabel="Mobile message input field" />
        ) : (
          <MobileResults tabs={tabs} content={content} />
        )}
      </div>
    </div>
  );
}
